import type { DefinitionProblem, DefinitionReadiness, GeneratedDefinition, GeneratedNodeAttribute } from "@keydials/api-client";
import { resolveFieldSelection, resolveSelection, type BuilderSelection, type FieldSelection, type SegmentNoun } from "@keydials/dial-workspace";
import { builderCopy as copy, fieldName, fill, nounValues } from "./copy";

// ---------------------------------------------------------------------------
// The readiness pill's reading of the server's check (KEY-560). The server is the only judge of whether a dial can go live, and
// the pill counts exactly what it sent: this only puts the answer into the builder's words and says which problems can be
// jumped to. A problem the server pins to a section or an action point still on the wheel, or to a field still on the dial, gets
// "Fix this"; everything else (most checks still report against the whole dial) is listed in the server's own words. The answer
// is read again after every save, and one that arrives after a newer one is dropped.
// ---------------------------------------------------------------------------

export interface ReadinessLine {
  /** Unique within one answer. */
  id: string;
  sentence: string;
  /** What "Fix this" selects: a section or an action point that's still on the wheel, or a field that's still on the dial. */
  jump: BuilderSelection | null;
}

export interface ReadinessView {
  ready: boolean;
  /** The pill's words. */
  label: string;
  /** Problems on a section or an action point, in the wheel's order, then problems on a field, in the dial's field order. */
  jumpable: ReadinessLine[];
  /** Everything else, in the order the server sent it. */
  general: ReadinessLine[];
}

/** A server message as a sentence. Most are written to follow "can't publish:", so they start in lower case with no full stop. */
export function asSentence(message: string): string {
  const text = message.trim();
  if (!text) return text;
  const capitalised = text[0].toUpperCase() + text.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

function targetOf(problem: DefinitionProblem): BuilderSelection | null {
  const { level, sectionKey, segmentKey } = problem.target;
  if (level === "Section" && sectionKey) return { level: "section", sectionKey };
  if (level === "Segment" && sectionKey && segmentKey) return { level: "segment", sectionKey, segmentKey };
  return null;
}

const titleOf = (field: GeneratedNodeAttribute): string | null => field.fieldsetTitle?.trim() || null;

/**
 * The field a field problem is on. The server names a field by its key alone, and a key is only unique within its level, so the
 * level is read from the dial: the item field for a scope on no items; for a scope on no sections or action points, the section
 * field when one with that key is limited to some sections, otherwise the action point field; for a group in two places, the
 * first field with that key under a heading, preferring one whose heading is used at another level too.
 */
function fieldTargetOf(problem: DefinitionProblem, model: GeneratedDefinition): FieldSelection | null {
  const key = problem.target.key;
  if (problem.target.level !== "Attribute" || !key) return null;
  const fields = model.nodeAttributes ?? [];
  const at = (target: string): FieldSelection => ({ level: "field", target, key });

  if (problem.code === "attribute.on-no-items") return at("Item");
  if (problem.code === "attribute.on-no-nodes") {
    return at(fields.some((f) => f.target === "Section" && f.key === key && f.appliesToKeys?.length) ? "Section" : "Segment");
  }
  if (problem.code === "attribute.fieldset-split") {
    const grouped = fields.filter((f) => f.key === key && titleOf(f) !== null);
    const split = grouped.find((f) => fields.some((other) => other.target !== f.target && titleOf(other) === titleOf(f)));
    const field = split ?? grouped[0];
    return field ? at(field.target) : null;
  }
  return null;
}

/** The builder's sentence for a problem on a field, naming the field as the author named it, or its group by its heading. */
function fieldSentenceFor(problem: DefinitionProblem, field: GeneratedNodeAttribute, words: Record<string, string>): string {
  const details = copy.details;
  const name = fieldName(field.label);
  if (problem.code === "attribute.on-no-items") return fill(details.fieldOnNoItem, { name });
  if (problem.code === "attribute.on-no-nodes") {
    return fill(field.target === "Section" ? details.fieldOnNoSection : details.fieldOnNoSegment, { ...words, name });
  }
  if (problem.code === "attribute.fieldset-split") return fill(details.groupSplit, { name: titleOf(field) ?? name });
  return asSentence(problem.message);
}

/** A problem with nowhere to jump: in the builder's words where it has them, otherwise the server's. */
function generalSentenceFor(problem: DefinitionProblem): string {
  if (problem.code === "dial.no-sections") return copy.build.problems.noSections;
  if (problem.code === "dimension.on-no-items") return copy.details.scaleOnNoItem;
  return asSentence(problem.message);
}

/**
 * The builder's own sentence for the problems it knows, naming things as the author named them (the server names an action
 * point by its short label). Anything else keeps the server's words.
 */
function sentenceFor(problem: DefinitionProblem, sectionName: string, segmentName: string | null, words: Record<string, string>): string {
  const problems = copy.build.problems;
  if (problem.code === "section.no-segments") {
    return sectionName ? fill(problems.noSegments, { ...words, name: sectionName }) : fill(problems.noSegmentsUnnamed, words);
  }
  if (problem.code === "segment.no-items") {
    return segmentName ? fill(problems.noItems, { name: segmentName }) : fill(problems.noItemsUnnamed, words);
  }
  return asSentence(problem.message);
}

/** What the pill shows for one readiness answer, against the dial as it is now. */
export function readinessView(readiness: DefinitionReadiness, model: GeneratedDefinition, noun: SegmentNoun): ReadinessView {
  const words = nounValues(noun);
  // Ordered by: the wheel before the fields, then the section (or the field's place in the dial), then the action point.
  const jumpable: Array<{ line: ReadinessLine; order: [number, number, number] }> = [];
  const general: ReadinessLine[] = [];

  readiness.problems.forEach((problem, index) => {
    const id = `${index}:${problem.code}`;
    // An answer read before the last edit can name something removed since. It's listed as the server said it, with nowhere to jump.
    const field = resolveFieldSelection(model, fieldTargetOf(problem, model));
    if (field) {
      const sentence = fieldSentenceFor(problem, model.nodeAttributes![field.fieldIndex], words);
      jumpable.push({ line: { id, sentence, jump: field.selection }, order: [1, field.fieldIndex, 0] });
      return;
    }
    const wanted = targetOf(problem);
    const resolved = wanted ? resolveSelection(model, wanted) : null;
    if (wanted && resolved && resolved.selection.level === wanted.level) {
      const section = model.sections[resolved.sectionIndex];
      const segment = resolved.segmentIndex === undefined ? null : section.segments[resolved.segmentIndex];
      jumpable.push({
        line: { id, sentence: sentenceFor(problem, section.label.trim(), segment ? segment.name.trim() : null, words), jump: resolved.selection },
        order: [0, resolved.sectionIndex, resolved.segmentIndex ?? -1],
      });
      return;
    }
    general.push({ id, sentence: generalSentenceFor(problem), jump: null });
  });

  jumpable.sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1] || a.order[2] - b.order[2]);

  const count = readiness.problems.length;
  return {
    ready: count === 0,
    label: count === 0 ? copy.build.ready : count === 1 ? copy.build.readinessOne : fill(copy.build.readinessMany, { n: count }),
    jumpable: jumpable.map((entry) => entry.line),
    general,
  };
}

export interface ReadinessFeed {
  /** Pass the draft session's save count after every change. It asks the server again only when the count has grown. */
  saved(saves: number): void;
  /** Drops any answer still on its way. */
  stop(): void;
}

/**
 * Reads readiness again after each save, and hands on only an answer newer than the last one shown. Two saves close together
 * can have their answers arrive the wrong way round, and the older one must not overwrite the newer. A failed read keeps the
 * answer already on screen: the next save asks again.
 */
export function createReadinessFeed(read: () => Promise<DefinitionReadiness>, onAnswer: (readiness: DefinitionReadiness) => void): ReadinessFeed {
  let asked = 0;
  let shown = 0;
  let stopped = false;

  return {
    saved(saves) {
      if (stopped || saves <= asked) return;
      asked = saves;
      const generation = saves;
      read().then(
        (readiness) => {
          if (stopped || generation <= shown) return;
          shown = generation;
          onAnswer(readiness);
        },
        () => undefined,
      );
    },
    stop() {
      stopped = true;
    },
  };
}
